import { tr } from "../i18n/LanguageContext";
import React, { useEffect } from "react";
import { Globe, Home, Blend } from "lucide-react";
import { Button } from "./ui/button";

const EventTypeToggles = ({ activeTypes, onTypesChange, resetTrigger = 0, initialTypes = { online: false, offline: false, hybrid: false }, className }) => {

  // Initialize with URL params if available, otherwise use prop values
  useEffect(() => {
    // Only set initial state if activeTypes is all false (meaning not set yet)
    const hasActiveTypes = activeTypes.online || activeTypes.offline || activeTypes.hybrid;
    const hasInitialTypes = initialTypes.online || initialTypes.offline || initialTypes.hybrid;
    
    if (!hasActiveTypes && hasInitialTypes) {
      onTypesChange(initialTypes);
    }
  }, []); // Run only once on mount

  // Reset all states when resetTrigger changes
  useEffect(() => {
    if (resetTrigger > 0) {
      onTypesChange({
        online: false,
        offline: false,
        hybrid: false
      });
    }
  }, [resetTrigger, onTypesChange]);

  const handleTypeToggle = (type) => {
    const newTypes = {
      ...activeTypes,
      [type]: !activeTypes[type]
    };
    onTypesChange(newTypes);
  };

  const handleKeyDown = (event, type) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTypeToggle(type);
    }
  };

  const getButtonConfig = (type) => {
    const isActive = activeTypes[type];
    
    switch (type) {
      case "online":
        return {
          label: isActive ? tr("Онлайн ✓") : tr("Онлайн"),
          icon: <Globe className="h-4 w-4" />,
          ariaLabel: "Toggle online events",
          activeClass: "bg-sky-600 hover:bg-sky-700 text-white shadow-md",
          inactiveClass: "border-sky-300 text-sky-700 hover:bg-sky-50 hover:border-sky-400"
        };
      case "offline":
        return {
          label: isActive ? tr("Оффлайн ✓") : tr("Оффлайн"),
          icon: <Home className="h-4 w-4" />,
          ariaLabel: "Toggle offline events",
          activeClass: "bg-amber-600 hover:bg-amber-700 text-white shadow-md",
          inactiveClass: "border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400"
        };
      case "hybrid":
        return {
          label: isActive ? tr("Гибрид ✓") : tr("Гибрид"),
          icon: <Blend className="h-4 w-4" />,
          ariaLabel: "Toggle hybrid events",
          activeClass: "bg-purple-600 hover:bg-purple-700 text-white shadow-md",
          inactiveClass: "border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400"
        };
      default:
        return {};
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="text-sm font-medium text-gray-700 mb-2">
        {tr("Тип события")}
      </div>
      
      <div className="flex flex-col sm:flex-row gap-3">
        {["online", "offline", "hybrid"].map((type) => {
          const config = getButtonConfig(type);
          const isActive = activeTypes[type];
          
          return (
            <Button
              key={type}
              onClick={() => handleTypeToggle(type)}
              onKeyDown={(e) => handleKeyDown(e, type)}
              variant={isActive ? "default" : "outline"}
              aria-label={config.ariaLabel}
              aria-pressed={isActive}
              role="button"
              tabIndex={0}
              className={`flex items-center gap-2 whitespace-nowrap transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 ${
                isActive ? config.activeClass : config.inactiveClass
              }`}
            >
              {config.icon}
              {tr(config.label)}
            </Button>
          );
        })}
      </div>

      {/* Active Types Display */}
      {(activeTypes.online || activeTypes.offline || activeTypes.hybrid) && (
        <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
          <span className="font-medium">{tr("Показать:")} </span>
          <span className="text-gray-800">
            {[
              activeTypes.online && tr("🌐 Онлайн"),
              activeTypes.offline && tr("🏠 Оффлайн"), 
              activeTypes.hybrid && tr("🔄 Гибрид")
            ].filter(Boolean).join(" + ")} {tr("события")}
          </span>
        </div>
      )}
    </div>
  );
};

export default EventTypeToggles;